export type CssVariables = {
  [key: string]: string;
};
export type ParentElement = ShadowRoot | Document | HTMLElement;
export type StyleTag = HTMLStyleElement | HTMLLinkElement;

function createCssVariablesSnippet(cssVariables: CssVariables = {}): string {
  if (Object.keys(cssVariables).length === 0) return '';
  const variableArray = [];
  for (const [variableName, variableValue] of Object.entries(cssVariables)) {
    variableArray.push(`\t--${variableName}: ${variableValue};`);
  }
  return `
    :host {
      ${variableArray.join('\n')}
    }`;
}

/**
 * Creates a object HTMLStyleElement. The content of the element is determined by the
 * arguments styleFileContent and cssVariables.
 * If the argument cssVariables is set, the function will format correctly and prepend
 * the content of the cssVariables map to the textContent of the element created.
 * @param styleFileContent
 * @param styleElementId
 * @param cssVariables
 * @returns {HTMLStyleElement}
 */
export function createStyleTag(
  styleFileContent: string | null,
  styleElementId: string,
  cssVariables: CssVariables | null = null
): HTMLStyleElement {
  const styleElement = document.createElement('style');
  if (cssVariables) styleElement.textContent += `${createCssVariablesSnippet(cssVariables)}\n`;
  if (styleFileContent) styleElement.textContent += styleFileContent;
  styleElement.id = styleElementId;
  return styleElement;
}

export function createLinkTag(url: string): HTMLLinkElement {
  const linkElement = document.createElement('link');
  linkElement.href = url;
  linkElement.rel = 'stylesheet';
  linkElement.type = 'text/css';
  return linkElement;
}

/**
 * This function check if there is a other LINK/STYLE element with the
 * same url/content of the target element has already been added as direct
 * child of the target parent.
 * The elements are matched based on some idempotency attributes:
 *  - In the case of the LINK elements, we check if there is another LINK element with the same
 * URL on the href attribute.
 * - For the STYLE elements, we check if there is another STYLE element with the same id attribute.
 * @param parentElement The parent element against we gonna check if our element is already a
 * @param element Element that we gonna check if is a child or not
 * @returns
 */
function checkIfStyleIsChild(parentElement: ParentElement, element: StyleTag) {
  let idempotencyAttr: string | null, idempotencyValue: string | null;
  if (element instanceof HTMLStyleElement) {
    idempotencyAttr = 'id';
    idempotencyValue = element.id;
  } else if (element instanceof HTMLLinkElement) {
    idempotencyAttr = 'href';
    idempotencyValue = element.href;
  } else {
    idempotencyAttr = null;
    idempotencyValue = null;
  }
  if (!idempotencyAttr && !idempotencyValue) return false;
  return !parentElement.querySelector(`:scope > ${element.tagName}[${idempotencyAttr}="${idempotencyValue}"]`);
}

/**
 * This function append a LINK/TAG element to the end of the children list
 * of the parent element.
 * If there is already a LINK/TAG element with the same url/content, the element
 * is not added.
 * @param parentElement
 * @param element
 */
export function appendTagToElement(parentElement: ParentElement, element: StyleTag) {
  if (checkIfStyleIsChild(parentElement, element)) parentElement.appendChild(element);
}
